"use client";

import { c as _c } from "react-compiler-runtime";
import { Loader2 } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import ImageCropEditor from "@/components/ui/ImageCropEditor/ImageCropEditor";
import { Text } from "@/components/primitives";
import AppImage from "@/components/primitives/Image/Image";
import { useAnalytics } from "@/core/analytics";
import { queryClient } from "@/core/caching";
import { getDeviceInfo } from "@/core/device";
import { type Dictionary, useTranslation } from "@/core/i18n";
import { useI18n } from "@/core/i18n/useI18n/useI18n";
import { logger } from "@/core/logging";
import { compressImageFile } from "@/core/media";
import { captureAssessmentConsents } from "@/features/assessments/consent/consentUtils";
import { deriveConsentLanguageCode, getBiometricConsentCheckboxPrefixKey, getBiometricConsentItemsForRegion } from "@/features/assessments/consent/regionalConsent";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { AESTHETICS_DEMO_PERSONAS, fetchDemoImageAsFile } from "@/features/assessments/demo/demoPersonas";
import { DemoPersonaSelector } from "@/features/assessments/demo/DemoPersonaSelector";
import { usePlatformDataPrivacyRegion } from "@/features/platform/usePlatformDataPrivacyRegion";
import type { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { getRetrieveCurrentAssessmentQueryKey, setAssessmentPhotoDeviceInformation } from "@orval/ageless-assessments/ageless-assessments";
import { setAssessmentPhotoCaptureComplete } from "@orval/ageless-imaging/ageless-imaging";
import { CAPTURE_COMPRESS_MAX_SIZE } from "@/features/camera/captureConstants";
import { useSharedFaceLandmarker } from "@/features/facialRecognition";
import { AestheticsPhotoUploader, FACE_OUTLINE_FRAME_CLASS } from "../components/AestheticsPhotoUploader/AestheticsPhotoUploader";
import { ConsentOverlay } from "../components/ConsentOverlay";
import { uploadFrontalFacialImageForAssessment, uploadProfileFacialImageForAssessment } from "../hooks/photoApi";
import { useConsentGate } from "../hooks/useConsentGate";
import { useDemoMode } from "../../../demo/useDemoMode";
import { hasAssessmentConsentInCache } from "@/features/assessments/consent/consentUtils";
import { CaptureHeader } from "./components/CaptureHeader";
import { getValidationIssueTranslationKeyWithFallback } from "./components/validationIssueTranslations";
import { FrontFacialUploadValidationError, validateFrontFacialUpload } from "./photoUploadNormalization";
import { waitForFrontalFacialImageValidation } from "./photoValidationGuard";
const EDITOR_MAX_DIMENSION = CAPTURE_COMPRESS_MAX_SIZE * 2;
const EDITOR_JPEG_QUALITY = 0.92;
const UPLOAD_CROP_ASPECT_RATIO = 3 / 4;
const IMAGE_PREPARE_TIMEOUT_MS = 30_000;
const FACE_DETECTION_TIMEOUT_MS = 20_000;
function withTimeout<T>(promise: Promise<T>, timeoutMs: number, timeoutMessage: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(timeoutMessage));
    }, timeoutMs);
    promise.then(value => {
      clearTimeout(timeoutId);
      resolve(value);
    }, (error: unknown) => {
      clearTimeout(timeoutId);
      reject(error);
    });
  });
}
type PhotoUploadFlowProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  goToNextStep: () => void;
  goToStart: () => void;
  onBackToTips?: () => void;
};
type UploadStage = {
  stage: "consent";
} | {
  stage: "frontUpload";
};
type PreparedUpload = {
  imageSrc: string;
  fileName: string;
};
export function PhotoUploadFlow(t0) {
  const $ = _c(21);
  const {
    assessment,
    headers,
    goToNextStep,
    goToStart,
    onBackToTips
  } = t0;
  const {
    flowType,
    subdomainSlug
  } = useAssessmentStep();
  let t1;
  if ($[0] !== flowType || $[1] !== subdomainSlug) {
    t1 = {
      subdomainSlug,
      flowType
    };
    $[0] = flowType;
    $[1] = subdomainSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isDemoMode = useDemoMode(t1);
  let t2;
  if ($[3] !== assessment.frontFaceImageUrl || $[4] !== assessment.id || $[5] !== assessment.uploadedFrontFaceImage || $[6] !== flowType) {
    t2 = Boolean(assessment.uploadedFrontFaceImage) || Boolean(assessment.frontFaceImageUrl) || hasAssessmentConsentInCache(assessment.id, flowType);
    $[3] = assessment.frontFaceImageUrl;
    $[4] = assessment.id;
    $[5] = assessment.uploadedFrontFaceImage;
    $[6] = flowType;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const hasExistingConsent = t2;
  let t3;
  if ($[8] !== hasExistingConsent) {
    t3 = () => hasExistingConsent ? {
      stage: "frontUpload"
    } : {
      stage: "consent"
    };
    $[8] = hasExistingConsent;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const [current, setCurrent] = useState(t3);
  switch (current.stage) {
    case "consent":
      {
        let t4;
        if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
          t4 = () => setCurrent({
            stage: "frontUpload"
          });
          $[10] = t4;
        } else {
          t4 = $[10];
        }
        let t5;
        if ($[11] !== assessment || $[12] !== goToStart || $[13] !== headers) {
          t5 = <UploadConsentStage assessment={assessment} headers={headers} onAccepted={t4} onDeclined={goToStart} />;
          $[11] = assessment;
          $[12] = goToStart;
          $[13] = headers;
          $[14] = t5;
        } else {
          t5 = $[14];
        }
        return t5;
      }
    case "frontUpload":
      {
        let t4;
        if ($[15] !== assessment || $[16] !== goToNextStep || $[17] !== headers || $[18] !== isDemoMode || $[19] !== onBackToTips) {
          t4 = isDemoMode ? <DemoPairCaptureStage assessment={assessment} headers={headers} onBackToTips={onBackToTips} onComplete={goToNextStep} /> : <FrontPhotoUploadStage assessment={assessment} headers={headers} onBackToTips={onBackToTips} onComplete={goToNextStep} />;
          $[15] = assessment;
          $[16] = goToNextStep;
          $[17] = headers;
          $[18] = isDemoMode;
          $[19] = onBackToTips;
          $[20] = t4;
        } else {
          t4 = $[20];
        }
        return t4;
      }
  }
}
type UploadConsentStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onAccepted: () => void;
  onDeclined: () => void;
};
function UploadConsentStage(t0) {
  const $ = _c(34);
  const {
    assessment,
    headers,
    onAccepted,
    onDeclined
  } = t0;
  const analytics = useAnalytics();
  const {
    flowType,
    subdomainSlug
  } = useAssessmentStep();
  const {
    currentLang
  } = useI18n();
  const {
    t
  } = useTranslation();
  const {
    dataPrivacyRegion,
    isPending: isRegionPending
  } = usePlatformDataPrivacyRegion(subdomainSlug);
  let t1;
  if ($[0] !== dataPrivacyRegion || $[1] !== t) {
    t1 = dataPrivacyRegion ? getBiometricConsentItemsForRegion(dataPrivacyRegion, t) : undefined;
    $[0] = dataPrivacyRegion;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const biometricConsentItems = t1;
  let t2;
  if ($[3] !== dataPrivacyRegion) {
    t2 = dataPrivacyRegion ? {
      checkboxPrefix: getBiometricConsentCheckboxPrefixKey(dataPrivacyRegion)
    } : undefined;
    $[3] = dataPrivacyRegion;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const consentTranslationKeys = t2;
  let t3;
  if ($[5] !== analytics || $[6] !== flowType || $[7] !== onDeclined) {
    t3 = () => {
      analytics.trackEvent("assessment_data_capture_consent_declined", {
        flowType
      });
      onDeclined();
    };
    $[5] = analytics;
    $[6] = flowType;
    $[7] = onDeclined;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== analytics || $[10] !== flowType) {
    t4 = () => {
      analytics.trackEvent("assessment_data_capture_consent_given", {
        flowType
      });
    };
    $[9] = analytics;
    $[10] = flowType;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== assessment || $[13] !== currentLang || $[14] !== dataPrivacyRegion || $[15] !== flowType || $[16] !== headers || $[17] !== onAccepted) {
    t5 = async () => {
      if (!dataPrivacyRegion) {
        return;
      }
      const languageCode = deriveConsentLanguageCode(currentLang);
      await captureAssessmentConsents(assessment.id, flowType, languageCode, dataPrivacyRegion, {
        headers
      });
      onAccepted();
    };
    $[12] = assessment;
    $[13] = currentLang;
    $[14] = dataPrivacyRegion;
    $[15] = flowType;
    $[16] = headers;
    $[17] = onAccepted;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t5) {
    t6 = {
      onCancel: t3,
      trackConsent: t4,
      onConsentAccepted: t5
    };
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const {
    consentChecked,
    setConsentChecked,
    isVerifying,
    handleConsentAgree,
    handleConsentCancel
  } = useConsentGate(t6);
  const t7 = isVerifying || isRegionPending || !dataPrivacyRegion;
  let t8;
  if ($[23] !== handleConsentCancel) {
    t8 = open => {
      if (!open) {
        handleConsentCancel();
      }
    };
    $[23] = handleConsentCancel;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== biometricConsentItems || $[26] !== consentChecked || $[27] !== consentTranslationKeys || $[28] !== handleConsentAgree || $[29] !== handleConsentCancel || $[30] !== setConsentChecked || $[31] !== t7 || $[32] !== t8) {
    t9 = <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90"><ConsentOverlay open={true} biometricConsentItems={biometricConsentItems} consentChecked={consentChecked} isVerifying={t7} translationKeys={consentTranslationKeys} onCancel={handleConsentCancel} onConfirm={handleConsentAgree} onConsentChange={setConsentChecked} onOpenChange={t8} /></div>;
    $[25] = biometricConsentItems;
    $[26] = consentChecked;
    $[27] = consentTranslationKeys;
    $[28] = handleConsentAgree;
    $[29] = handleConsentCancel;
    $[30] = setConsentChecked;
    $[31] = t7;
    $[32] = t8;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  return t9;
}
type DemoPairCaptureStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onComplete: () => void;
  onBackToTips?: () => void;
};
type DemoPairPhase = "pickPersona" | "uploading" | "error";
function DemoPairCaptureStage({
  assessment,
  headers,
  onComplete,
  onBackToTips
}: DemoPairCaptureStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const [phase, setPhase] = useState<DemoPairPhase>("pickPersona");
  const [selectedPersonaId, setSelectedPersonaId] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const demoThumbnails = AESTHETICS_DEMO_PERSONAS.map(persona => ({
    id: persona.id,
    label: persona.label,
    secondaryThumbnailUrl: persona.profileImageUrl,
    thumbnailUrl: persona.frontImageUrl
  }));
  const selectedPersona = selectedPersonaId ? AESTHETICS_DEMO_PERSONAS.find(candidate => candidate.id === selectedPersonaId) ?? null : null;
  const handleDemoSelect = async (personaId: string) => {
    setSelectedPersonaId(personaId);
    setPhase("uploading");
    setUploadError(null);
    try {
      const persona_0 = AESTHETICS_DEMO_PERSONAS.find(candidate_0 => candidate_0.id === personaId);
      if (!persona_0) {
        throw new Error(`Demo persona is not configured: ${personaId}`);
      }
      const frontFile = await fetchDemoImageAsFile(persona_0.frontImageUrl, persona_0.frontFileName);
      const frontPhotoDataUrl = await readFileAsDataUrl(frontFile);
      const frontUploadResponse = await uploadFrontalFacialImageForAssessment({
        assessmentId: assessment.id,
        platformId: assessment.platformId,
        photoDataUrl: frontPhotoDataUrl,
        fileName: persona_0.frontFileName,
        requestOptions: {
          headers
        }
      });
      const profileFile = await fetchDemoImageAsFile(persona_0.profileImageUrl, persona_0.profileFileName);
      const profilePhotoDataUrl = await readFileAsDataUrl(profileFile);
      const profileUploadResponse = await uploadProfileFacialImageForAssessment({
        assessmentId: assessment.id,
        platformId: assessment.platformId,
        photoDataUrl: profilePhotoDataUrl,
        fileName: persona_0.profileFileName,
        requestOptions: {
          headers
        }
      });
      await setAssessmentPhotoCaptureComplete(assessment.id, {
        frontalFacialImageId: frontUploadResponse.imageId,
        selectedProfileFacialImageId: profileUploadResponse.imageId,
        selectedProfileSide: persona_0.profileSide
      } as {
        frontalFacialImageId: string;
        selectedProfileFacialImageId: string;
        selectedProfileSide: "left" | "right";
      }, {
        headers
      });
      try {
        const deviceInfo = getDeviceInfo();
        await setAssessmentPhotoDeviceInformation(assessment.id, {
          deviceInfo,
          cameraInfo: {
            hasCamera: false,
            cameras: null,
            facingModes: null,
            maxResolution: null
          }
        }, {
          headers
        });
      } catch (error_0) {
        logger.error("demo_pair_device_info_failed", undefined, error_0 as Error);
      }
      await queryClient.invalidateQueries({
        queryKey: getRetrieveCurrentAssessmentQueryKey()
      });
      analytics.trackEvent("assessment_photo_captured", {
        flowType
      });
      analytics.trackEvent("assessment_front_photo_upload_completed", {
        flowType
      });
      onComplete();
    } catch (error) {
      logger.error("demo_pair_upload_failed", undefined, error as Error);
      setUploadError(t("assessmentSteps.studioSession.photoUpload.uploadFailed"));
      setPhase("error");
    }
  };
  const isUploading = phase === "uploading";
  return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
      <CaptureHeader title={t("assessmentSteps.studioSession.photoUpload.demoPairTitle")} onBack={onBackToTips} />

      <div className="relative flex min-h-0 flex-1 items-start justify-center overflow-y-auto px-3 py-8 sm:items-center sm:px-6">
        <div className="relative w-full max-w-lg">
          <AnimatePresence mode="wait">
            {isUploading && selectedPersona ? <DemoPairUploadProgressPreview key="demo-uploading" frontImageUrl={selectedPersona.frontImageUrl} label={t("assessmentSteps.studioSession.photoUpload.uploading")} profileImageUrl={selectedPersona.profileImageUrl} /> : <motion.div animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} initial={{
            opacity: 0,
            y: 12
          }} key="demo-picker" transition={{
            duration: 0.25
          }}>
                {uploadError ? <div className="mb-4 rounded-2xl bg-white/10 px-4 py-3 text-center">
                    <Text component="p" text={uploadError} tone="white" variant="body-sm" />
                  </div> : null}
                <div className="mb-2 text-center">
                  <Text component="p" text={t("assessmentSteps.studioSession.photoUpload.demoPairPrompt")} tone="white" variant="body-sm" weight="medium" />
                </div>
                <DemoPersonaSelector disabled={false} personas={demoThumbnails} onSelect={handleDemoSelect} />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      <div className="pb-safe relative z-10 w-full px-4 py-6 sm:px-6" />
    </div>;
}
type FrontPhotoUploadStageProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  onComplete: () => void;
  onBackToTips?: () => void;
};
type FrontUploadPhase = "pickFile" | "preparing" | "cropping" | "checkingFace" | "verifying" | "error";
function FrontPhotoUploadStage({
  assessment,
  headers,
  onComplete,
  onBackToTips
}: FrontPhotoUploadStageProps): React.ReactElement {
  const {
    t
  } = useTranslation();
  const analytics = useAnalytics();
  const {
    flowType
  } = useAssessmentStep();
  const {
    detectFaceInImage,
    isInitialized
  } = useSharedFaceLandmarker();
  const [phase, setPhase] = useState<FrontUploadPhase>("pickFile");
  const [preparedUpload, setPreparedUpload] = useState<PreparedUpload | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [validationIssues, setValidationIssues] = useState<string[]>([]);
  const resetToPicker = () => {
    setPhase("pickFile");
    setPreparedUpload(null);
    setThumbnailUrl(null);
    setUploadError(null);
    setValidationIssues([]);
  };
  const failWithMessage = (message: string, issues: string[] = []) => {
    setUploadError(message);
    setValidationIssues(issues);
    setPreparedUpload(null);
    setThumbnailUrl(null);
    setPhase("error");
  };
  const handleFileSelected = async (file: File) => {
    setUploadError(null);
    setValidationIssues([]);
    setPhase("preparing");
    try {
      const editorFile = await withTimeout(compressImageFile(file, EDITOR_MAX_DIMENSION, EDITOR_JPEG_QUALITY), IMAGE_PREPARE_TIMEOUT_MS, "Timed out while preparing the photo for cropping.");
      const imageSrc = await readFileAsDataUrl(editorFile);
      setPreparedUpload({
        imageSrc,
        fileName: file.name
      });
      setPhase("cropping");
    } catch (error) {
      logger.error("photo_upload_prepare_failed", undefined, error as Error);
      failWithMessage(t("assessmentSteps.studioSession.photoUpload.uploadFailed"));
    }
  };
  const handleCropComplete = async (croppedFile: File) => {
    setPhase("checkingFace");
    setUploadError(null);
    setValidationIssues([]);
    setThumbnailUrl(null);
    try {
      if (!preparedUpload) {
        throw new Error("Cannot upload cropped photo before a front facial image is prepared.");
      }
      if (!isInitialized) {
        throw new Error("Face detection is not ready for photo upload validation.");
      }
      const fileName = preparedUpload.fileName;
      const fileWithOriginalName = new File([croppedFile], fileName, {
        type: croppedFile.type,
        lastModified: croppedFile.lastModified
      });
      const photoDataUrl = await readFileAsDataUrl(fileWithOriginalName);
      await withTimeout(validateFrontFacialUpload({
        dataUrl: photoDataUrl,
        detectFaceInImage
      }), FACE_DETECTION_TIMEOUT_MS, "Timed out while checking the photo for a face.");
      setThumbnailUrl(photoDataUrl);
      setPhase("verifying");
      const uploadResponse = await uploadFrontalFacialImageForAssessment({
        assessmentId: assessment.id,
        platformId: assessment.platformId,
        photoDataUrl,
        fileName,
        requestOptions: {
          headers
        }
      });
      analytics.trackEvent("assessment_photo_captured", {
        flowType
      });
      const validation = await waitForFrontalFacialImageValidation({
        imageId: uploadResponse.imageId,
        requestOptions: {
          headers
        }
      });
      if (validation.isUsable === false) {
        const translatedIssues = validation.issueKeys.map((issueKey: string) => t(getValidationIssueTranslationKeyWithFallback(issueKey, "assessmentSteps.studioSession.preview.serverRejectedFallback")));
        failWithMessage(getPrimaryValidationMessage(translatedIssues, t), translatedIssues.slice(1));
        return;
      }
      await setAssessmentPhotoCaptureComplete(assessment.id, {
        frontalFacialImageId: uploadResponse.imageId
      }, {
        headers
      });
      try {
        const deviceInfo = getDeviceInfo();
        await setAssessmentPhotoDeviceInformation(assessment.id, {
          deviceInfo,
          cameraInfo: {
            hasCamera: false,
            cameras: null,
            facingModes: null,
            maxResolution: null
          }
        }, {
          headers
        });
      } catch (error_1) {
        logger.error("photo_upload_device_info_failed", undefined, error_1 as Error);
      }
      await queryClient.invalidateQueries({
        queryKey: getRetrieveCurrentAssessmentQueryKey()
      });
      analytics.trackEvent("assessment_front_photo_upload_completed", {
        flowType
      });
      onComplete();
    } catch (error_0) {
      logger.error("photo_upload_front_failed", undefined, error_0 as Error);
      failWithMessage(getUploadErrorMessage(error_0, t));
    }
  };
  const isProgressPhase = phase === "preparing" || phase === "checkingFace" || phase === "verifying";
  const isPickerPhase = phase === "pickFile" || phase === "error";
  return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
      <CaptureHeader onBack={onBackToTips} />

      {phase === "cropping" && preparedUpload ? <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-y-contain [padding-bottom:calc(env(safe-area-inset-bottom)+1rem)]">
          <div className="mx-auto w-full max-w-sm px-3 py-3 sm:px-6 sm:py-4">
            <AnimatePresence mode="wait">
              <motion.div animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -8
          }} initial={{
            opacity: 0,
            y: 12
          }} key="cropping" transition={{
            duration: 0.25
          }}>
                <ImageCropEditor cancelLabel={t("wellnessSteps.intake.cancel")} confirmLabel={t("wellnessSteps.intake.cropAndContinue")} cropAspectRatio={UPLOAD_CROP_ASPECT_RATIO} dragToCropLabel={t("wellnessSteps.intake.dragToCrop")} imageSrc={preparedUpload.imageSrc} quality={0.85} targetHeight={CAPTURE_COMPRESS_MAX_SIZE} targetWidth={CAPTURE_COMPRESS_MAX_SIZE} tone="overlay" onCancel={resetToPicker} onCropComplete={handleCropComplete} />
              </motion.div>
            </AnimatePresence>
          </div>
        </div> : isProgressPhase ? <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-3 py-4">
          <AnimatePresence mode="wait">
            <UploadProgressPreview key="progress" label={phase === "preparing" ? t("assessmentSteps.studioSession.photoUpload.preparingPhoto") : phase === "checkingFace" ? t("assessmentSteps.studioSession.photoUpload.checking") : t("assessmentSteps.studioSession.photoUpload.uploading")} thumbnailUrl={thumbnailUrl} />
          </AnimatePresence>
        </div> : <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto overscroll-y-contain px-3 py-2 [padding-bottom:calc(env(safe-area-inset-bottom)+0.5rem)] sm:gap-4 sm:px-6">
          <div className="shrink-0 px-2 text-center">
            <Text component="p" text={t("assessmentSteps.studioSession.photoUpload.frontSubtitle")} tone="white" variant="body-sm" weight="light" />
          </div>
          <AnimatePresence mode="wait">
            {isPickerPhase ? <UploaderPanel disabled={false} errorMessage={phase === "error" ? uploadError : null} key={phase === "error" ? "error" : "picker"} validationIssues={phase === "error" ? validationIssues : []} onFileSelected={handleFileSelected} onReset={phase === "error" ? resetToPicker : undefined} /> : null}
          </AnimatePresence>
        </div>}
    </div>;
}
type UploaderPanelProps = {
  disabled: boolean;
  errorMessage: string | null;
  validationIssues: string[];
  onFileSelected: (file: File) => void;
  onReset?: () => void;
};
function UploaderPanel(t0) {
  const $ = _c(10);
  const {
    disabled,
    errorMessage,
    validationIssues,
    onFileSelected,
    onReset
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 1,
      y: 0
    };
    t2 = {
      opacity: 0,
      y: -8
    };
    t3 = {
      opacity: 0,
      y: 12
    };
    t4 = {
      duration: 0.25
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== disabled || $[5] !== errorMessage || $[6] !== onFileSelected || $[7] !== onReset || $[8] !== validationIssues) {
    t5 = <motion.div animate={t1} exit={t2} initial={t3} transition={t4}><AestheticsPhotoUploader disabled={disabled} errorMessage={errorMessage} validationIssues={validationIssues} onFileSelected={onFileSelected} onReset={onReset} /></motion.div>;
    $[4] = disabled;
    $[5] = errorMessage;
    $[6] = onFileSelected;
    $[7] = onReset;
    $[8] = validationIssues;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
type UploadProgressPreviewProps = {
  label: string;
  thumbnailUrl: string | null;
};
function UploadProgressPreview(t0) {
  const $ = _c(11);
  const {
    label,
    thumbnailUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    t3 = {
      opacity: 0
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== thumbnailUrl) {
    t4 = <div className="absolute inset-0 overflow-hidden rounded-[50%] border-2 border-white/40 bg-neutral-900">{thumbnailUrl ? <><AppImage fill={true} alt="" className="object-cover opacity-40 blur-xl" src={thumbnailUrl} /><AppImage fill={true} alt="Uploaded front facial photo" className="object-cover" src={thumbnailUrl} /></> : <div className="flex h-full w-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-white/70" /></div>}</div>;
    $[3] = thumbnailUrl;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Loader2 className="h-4 w-4 animate-spin" />;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== label) {
    t6 = <div className="absolute inset-x-0 -bottom-10 flex justify-center sm:-bottom-12"><div className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm">{t5}<Text component="span" text={label} variant="body-sm" weight="medium" /></div></div>;
    $[6] = label;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== t4 || $[9] !== t6) {
    t7 = <motion.div animate={t1} className="mx-auto flex flex-col items-center justify-center pb-10 sm:pb-12" exit={t2} initial={t3}><div className={FACE_OUTLINE_FRAME_CLASS}>{t4}{t6}</div></motion.div>;
    $[8] = t4;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  return t7;
}
type DemoPairUploadProgressPreviewProps = {
  frontImageUrl: string;
  label: string;
  profileImageUrl: string;
};
function DemoPairUploadProgressPreview(t0) {
  const $ = _c(16);
  const {
    frontImageUrl,
    label,
    profileImageUrl
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      opacity: 1
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      opacity: 0
    };
    t3 = {
      opacity: 0
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] !== frontImageUrl) {
    t4 = <DemoPairUploadProgressTile imageUrl={frontImageUrl} />;
    $[3] = frontImageUrl;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== profileImageUrl) {
    t5 = <DemoPairUploadProgressTile imageUrl={profileImageUrl} />;
    $[5] = profileImageUrl;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t4 || $[8] !== t5) {
    t6 = <div className="flex w-full items-center justify-center gap-3">{t4}{t5}</div>;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Loader2 className="h-4 w-4 animate-spin" />;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== label) {
    t8 = <div className="bg-background/80 flex items-center gap-2 rounded-full px-4 py-2 shadow-lg backdrop-blur-sm">{t7}<Text component="span" text={label} variant="body-sm" weight="medium" /></div>;
    $[11] = label;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <motion.div animate={t1} className="flex aspect-3/4 flex-col items-center justify-center gap-6 overflow-hidden rounded-3xl bg-neutral-900 px-4 py-8" exit={t2} initial={t3}>{t6}{t8}</motion.div>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  return t9;
}
type DemoPairUploadProgressTileProps = {
  imageUrl: string;
};
function DemoPairUploadProgressTile(t0) {
  const $ = _c(8);
  const {
    imageUrl
  } = t0;
  let t1;
  let t2;
  if ($[0] !== imageUrl) {
    t1 = <AppImage fill={true} alt="" className="object-cover opacity-40 blur-lg" src={imageUrl} />;
    t2 = <AppImage fill={true} alt="" className="object-cover" src={imageUrl} />;
    $[0] = imageUrl;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="absolute inset-0 bg-black/30" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-white" /></div>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2) {
    t5 = <div className="relative aspect-3/4 w-1/2 overflow-hidden rounded-2xl bg-neutral-800">{t1}{t2}{t3}{t4}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}
function getUploadErrorMessage(error: unknown, t: (key: keyof Dictionary) => string): string {
  if (error instanceof FrontFacialUploadValidationError) {
    switch (error.reason) {
      case "no_face":
        return t("assessmentSteps.studioSession.photoUpload.errorNoFace");
      case "multiple_faces":
        return t("assessmentSteps.studioSession.photoUpload.errorMultipleFaces");
      case "non_frontal_pose":
        return t("assessmentSteps.studioSession.photoUpload.errorNonFrontalPose");
    }
  }
  return t("assessmentSteps.studioSession.photoUpload.uploadFailed");
}
function getPrimaryValidationMessage(translatedIssues: string[], t: (key: keyof Dictionary) => string): string {
  if (translatedIssues.length > 0) {
    return translatedIssues[0];
  }
  return t("assessmentSteps.studioSession.photoUpload.uploadFailed");
}